import { type AuthUser, type ProductCode } from '@gb-ticket/shared-types';
import type { ComponentType } from 'react';

import {
  activeProductCode,
  productNavRegistration,
  visibleMobileSlots,
  type ProductNavSlot,
} from '@/platform/lib/product-nav';
import { hasIntranetProduct, intranetEntryPath } from '@/platform/lib/post-login';

export type ResolvedMobileTab =
  | { kind: 'link'; id: string; label: string; to: string }
  | { kind: 'action'; id: string; label: string; Component: ComponentType }
  | { kind: 'empty'; id: string; label: string };

function slotToTab(slot: ProductNavSlot): ResolvedMobileTab {
  if (slot.kind === 'link') {
    return { kind: 'link', id: slot.id, label: slot.label, to: slot.to };
  }
  return { kind: 'action', id: slot.id, label: slot.label, Component: slot.Component };
}

function emptyTab(index: number): ResolvedMobileTab {
  return { kind: 'empty', id: `empty-${index}`, label: '' };
}

function padThree(
  tabs: ResolvedMobileTab[],
): [ResolvedMobileTab, ResolvedMobileTab, ResolvedMobileTab] {
  const next = tabs.slice(0, 3);
  while (next.length < 3) {
    next.push(emptyTab(next.length));
  }
  return [next[0]!, next[1]!, next[2]!];
}

function newsTab(user: AuthUser): ResolvedMobileTab | null {
  if (!hasIntranetProduct(user)) {
    return null;
  }
  return { kind: 'link', id: 'news', label: 'News', to: intranetEntryPath(user) };
}

/**
 * Three frequent targets for the bottom bar after Menü.
 * News only on Übersicht and Intranet. Missing rights drop a target; later ones move left.
 */
export function resolveMobileContextTabs(
  pathname: string,
  user: AuthUser | null | undefined,
): [ResolvedMobileTab, ResolvedMobileTab, ResolvedMobileTab] {
  if (!user) {
    return padThree([]);
  }

  const code = activeProductCode(pathname);
  if (!code) {
    return padThree(newsTab(user) ? [newsTab(user)!] : []);
  }

  return resolveProductContextTabs(pathname, code, user);
}

function resolveProductContextTabs(
  pathname: string,
  code: ProductCode,
  user: AuthUser,
): [ResolvedMobileTab, ResolvedMobileTab, ResolvedMobileTab] {
  const slots = visibleMobileSlots(productNavRegistration(code), user).slice(0, 3).map(slotToTab);
  const intranet = pathname === '/intranet' || pathname.startsWith('/intranet/');
  if (intranet) {
    const news = newsTab(user);
    return padThree(news ? [news, ...slots] : slots);
  }
  return padThree(slots);
}
